import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import { useLanguage } from "../../context/languageContext";
import BulkUploadPopup from "../../components/BulkUploadPopup";
import { splitList } from "../../helper/listSeparator";

const RECOMMENDER_VALIDATION_RULES = [
    { field: 'recommender_name', type: 'string', message: 'Name is required' },
    { field: 'profession_industry', type: 'array', message: 'Profession names are required' },
    { field: 'recommended_genres', type: 'array', message: 'Genre names are required' },
    { field: 'recommended_books', type: 'array', message: 'Book names are required' },
    { field: 'about_recommender', type: 'string', message: 'About is required' },
];

const RECOMMENDER_COLUMNS = [
    { key: 'recommender_name', label: 'Name' },
    { key: 'profession_industry', label: 'Profession Names' },
    { key: 'recommended_genres', label: 'Genre Names' },
    // Keyed like the validation rule and the row field (`recommended_books`):
    // as `books_written`, a missing-books error had no column to show on.
    { key: 'recommended_books', label: 'Book Names' },
    { key: 'about_recommender', label: 'About' },
];

const BulkUploadRecommender = (props) => {
    const { isPopupVisible, handleClosePopup, onSuccess } = props;
    const { adminInfo } = useLanguage();

    const [loading, setLoading] = useState(false);

    // Several values in one cell are separated by ";" — a comma belongs to the
    // value ("Martin Luther King, Jr.", "Everyone Communicates, Few Connect").
    const parseListCell = splitList;

    const createProfessionObjects = (professions) => professions.map((profession) => ({ professionName: profession }));
    const createGenreObjects = (genres) => genres.map((genre) => ({ genreName: genre }));
    const createBookObjects = (books) => books.map((book) => ({ bookName: book }));

    const transformedRowValues = (row) => ({
        recommender_name: row.recommender_name?.trim() || "",
        about_recommender: row.about_recommender?.trim() || "",
        profession_industry: createProfessionObjects(parseListCell(row.profession_industry)),
        recommended_genres: createGenreObjects(parseListCell(row.recommended_genres)),
        recommended_books: createBookObjects(parseListCell(row.recommended_books)),
    });

    const getTableRowValues = (value) => (
        <>
            <td className="max_width">{value.recommender_name}</td>
            <td className="max_width">
                {value.profession_industry?.map((item, index) => (
                    <li className="list_styled" key={index}>{item.professionName}</li>
                ))}
            </td>
            <td className="max_width">
                {value.recommended_genres?.map((item, index) => (
                    <li className="list_styled" key={index}>{item.genreName}</li>
                ))}
            </td>
            <td className="max_width">
                {value.recommended_books?.map((item, index) => (
                    <li className="list_styled" key={index}>{item.bookName}</li>
                ))}
            </td>
            <td className="min_width">{value.about_recommender}</td>
        </>
    );

    const handleAddRecommenderData = async (values) => {
        setLoading(true);
        try {
            if (values.length >= 50) {
                toast.info("You can only add up to 50 recommenders at a time");
                setLoading(false);
                return;
            };

            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/recommend/bulk-add`, { values }, { headers });

            if (response.status === true) {
                toast.success(response.message);
                handleClosePopup();
                onSuccess();
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            console.error("handleAddRecommenderData catch error::", error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error;
            toast.error(errorMessage);
        } finally {
            setTimeout(() => setLoading(false), 300);
        }
    };


    return (
        <BulkUploadPopup
            isPopupVisible={isPopupVisible}
            handleClosePopup={handleClosePopup}
            title="Add Recommenders in Bulk through CSV"
            columns={RECOMMENDER_COLUMNS}
            validationRules={RECOMMENDER_VALIDATION_RULES}
            transformedRowValues={transformedRowValues}
            getTableRowValues={getTableRowValues}
            onSubmit={handleAddRecommenderData}
            label="Recommenders"
            isSubmit={loading}
        />
    );
};

export default BulkUploadRecommender;
